import {FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    CompaniesService,
    CompanyModel,
    CompanyExtendedModel
} from '@spa/api';

import {BaseControl, Helper} from '../../../../../shared/classes';

import {CompanyVatDetailsFormErrorStatesEnum} from '../../../enums';

import {
    CompanyVatDetailsControls,
    CompanyVatDetailsVatSelector,
    CompanyVatDetailsGiinSelector,
    CompanyVatDetailsLeiCodeSelector,
    CompanyVatDetailsLeiExpirationDateSelector,
    CompanyVatDetailsLeiCodeLength
} from '../../../../../../assets/descriptors';

export class CompanyVatDetailsForm {
    private readonly company: CompanyExtendedModel;
    private readonly companiesService: CompaniesService;

    public form: FormGroup;
    public controls: BaseControl[];
    public controlsObservable: BehaviorSubject<BaseControl[]>;

    constructor(company: CompanyExtendedModel, companiesService: CompaniesService) {
        this.company = company;
        this.companiesService = companiesService;
    }

    private checkForUpdate(model: CompanyModel): boolean {
        return (
            (this.company && model) &&
            (
                (this.company.lei !== model.lei) ||
                (this.company.leiExpiration !== model.leiExpiration) ||
                (this.company.giin !== model.giin) ||
                (this.company.vat !== model.vat)
            )
        );
    }

    initForm(): void {
        this.form = new FormGroup({});

        this.controls = CompanyVatDetailsControls().map((control: BaseControl) => {
            switch (control.name) {
                case CompanyVatDetailsLeiCodeSelector:
                    control.value = !this.company ? null : this.company.lei;
                    break;
                case CompanyVatDetailsLeiExpirationDateSelector:
                    control.value = !this.company ? null : this.company.leiExpiration;
                    break;
                case CompanyVatDetailsVatSelector:
                    control.value = !this.company ? null : this.company.vat;
                    break;
                case CompanyVatDetailsGiinSelector:
                    control.value = !this.company ? null : this.company.giin;
                    break;
            }

            this.form.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });

        this.controlsObservable = new BehaviorSubject(this.controls);
    }

    validateForm(messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            this.controls.map((control: BaseControl) => {
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    control.errors = Object.keys(formControl.errors).map((key: string) => {

                        return messageFunction(control.placeholder, CompanyVatDetailsLeiCodeLength)[key];
                    });
                }
            });

            return false;
        }

        return true;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        const model = new CompanyModel();

        model.id = this.company.id;
        model.name = this.company.name;
        model.organizationType = this.company.organizationType;
        model.dateOfIncorporation = this.company.dateOfIncorporation;
        model.financialInstitution = this.company.financialInstitution;
        model.miFIDCategory = this.company.miFidCategory;
        model.enrollChannel = this.company.enrollChannel;
        model.tradingCategory = this.company.tradingCategory;
        model.tenantId = this.company.tenantId;
        model.parentId = this.company.parentId;
        model.countryOfIncorporationId = this.company.countryOfIncorporation;
        model.lei = this.form.value[CompanyVatDetailsLeiCodeSelector];
        model.leiExpiration = this.form.value[CompanyVatDetailsLeiExpirationDateSelector];
        model.vat = this.form.value[CompanyVatDetailsVatSelector];
        model.giin = this.form.value[CompanyVatDetailsGiinSelector];
        model.alias = this.company.alias;
        model.oldClientId = this.company.oldClientId;
        model.forceAlias = false;
        if (this.checkForUpdate(model)) {
            return this.companiesService.update(model)
                .pipe(
                    map((response: ApiResponse<ApiError[] | null>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<null>(
                                response,
                                CompanyVatDetailsFormErrorStatesEnum.VatDetailsUpdateFailed
                            );
                        }

                        this.company.lei = model.lei;
                        this.company.leiExpiration = model.leiExpiration;
                        this.company.vat = model.vat;
                        this.company.giin = model.giin;

                        return Helper.createSuccessResponse<boolean>(true);
                    })
                );
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
